import { LocaleCode, isLocaleCode, normalizeLocaleCodeValue } from '@packages/i18n'
import type { MessageTree } from '@packages/i18n-messages'

type MessageLoader = () => Promise<MessageTree | string>

const FALLBACK_LOCALE: LocaleCode = LocaleCode.En

const localeRawMessageLoaders = import.meta.glob('~~/i18n/locales/TXT_*.json', {
  import: 'default',
  query: '?raw',
}) as Record<string, MessageLoader>

const localeRawMessageLoaderMap = new Map<LocaleCode, MessageLoader>()
const localeMessageCache = new Map<LocaleCode, Promise<MessageTree>>()

const isMessageTree = (value: unknown): value is MessageTree => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const extractLocaleCodeFromPath = (filePath: string) => {
  const fileName = filePath.split('/').at(-1) ?? filePath
  const localeToken = fileName.replace(/^TXT_/, '').replace(/\.json$/i, '')
  const normalizedLocale = normalizeLocaleCodeValue(localeToken)

  return isLocaleCode(normalizedLocale) ? normalizedLocale : null
}

for (const [filePath, loader] of Object.entries(localeRawMessageLoaders)) {
  const localeCode = extractLocaleCodeFromPath(filePath)

  if (localeCode !== null) {
    localeRawMessageLoaderMap.set(localeCode, loader)
  }
}

const loadLocaleMessages = async (locale: LocaleCode) => {
  const loader = localeRawMessageLoaderMap.get(locale)

  if (loader === undefined) {
    return {}
  }

  const loadedMessages = await loader()
  const messages = typeof loadedMessages === 'string' ? (JSON.parse(loadedMessages) as unknown) : loadedMessages

  return isMessageTree(messages) ? messages : {}
}

const getCachedLocaleMessages = (locale: LocaleCode) => {
  const cachedMessages = localeMessageCache.get(locale) ?? loadLocaleMessages(locale)

  localeMessageCache.set(locale, cachedMessages)

  return cachedMessages
}

const getMessageValue = (messages: MessageTree, key: string): unknown => {
  const message = key.split('.').reduce<unknown>((currentValue, part) => {
    if (isMessageTree(currentValue) && part in currentValue) {
      return currentValue[part]
    }

    return undefined
  }, messages)

  if (message !== undefined) {
    return message
  }

  const defaultMessages = messages.default
  return isMessageTree(defaultMessages) ? getMessageValue(defaultMessages, key) : undefined
}

const getCompiledMessageSource = (message: unknown) => {
  if (!isMessageTree(message)) {
    return ''
  }

  const loc = message.loc

  if (isMessageTree(loc) && typeof loc.source === 'string') {
    return loc.source
  }

  return getMessageAstText(message)
}

const getMessageAstText = (node: unknown): string => {
  if (!isMessageTree(node)) {
    return ''
  }

  if (typeof node.s === 'string') {
    return node.s
  }

  const children = Array.isArray(node.i) ? node.i : Array.isArray(node.c) ? node.c : null

  if (children !== null) {
    const separator = Array.isArray(node.c) ? ' | ' : ''
    return children.map(getMessageAstText).filter(Boolean).join(separator)
  }

  return getMessageAstText(node.b)
}

/**
 * 为 SEO 读取原始语言包，避免跨 server 边界导入与 `|` 被 vue-i18n 当作复数语法处理。
 */
export const getSeoLocaleMessage = async (locale: LocaleCode, key: string) => {
  const [localeMessages, fallbackMessages] = await Promise.all([
    getCachedLocaleMessages(locale),
    locale === FALLBACK_LOCALE ? Promise.resolve({}) : getCachedLocaleMessages(FALLBACK_LOCALE),
  ])
  const message = getMessageValue(localeMessages, key) ?? getMessageValue(fallbackMessages, key)
  const compiledMessageSource = getCompiledMessageSource(message)

  if (typeof message === 'string' && message) {
    return message
  }

  return compiledMessageSource || key
}
